import { Formik } from 'formik';
import { useMediaQuery } from 'react-responsive';
import InputErrorObserver from 'src/services/inputErrorObserver';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import { CircularProgress, Button, Box } from '@mui/material';

/**
 * WebStepperForm is a form component that allows users to progress through multiple steps.
 *
 * @param {array} children            - Children to be wrapped by form.
 * @param {boolean} disableNext       - Value used to disable the next button.
 * @param {object} initialValues      - Initial form values.
 * @param {boolean} isFirst           - Value used to change the next button to submit button.
 * @param {boolean} isLast            - Value used to disable the back button if true.
 * @param {function} onBack           - Event that will be fired when back button is clicked.
 * @param {function} onNext           - Event that will be fired when the next button is clicked.
 * @param {function} onSubmit         - Event that will be fired when submit button is clicked.
 * @param {object} schema             - Yup object schema.
 * @param {boolean} isLoading         - Value used to disable the next button and show a loading indicator.
 *
 * @returns {JSX.Element} 				    - The property type component.
 */
export default function WebStepperForm({
  children,
  disableNext = false,
  initialValues = {},
  isFirst = true,
  isLast = false,
  onBack = () => {},
  onNext = () => {},
  onSubmit = () => {},
  schema = {},
  isLoading = false,
}) {
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });

  return (
    <Formik
      enableReinitialize
      onSubmit={(formData) => (isLast ? onSubmit(formData) : onNext(formData))}
      initialValues={initialValues}
      validationSchema={schema}
    >
      {({ handleSubmit }) => {
        return (
          <>
            <div className="flex flex-grow flex-col gap-4 p-5 pb-10 lg:my-10">
              {/* Observer to allow for focusing the input field when there are errors in the form */}
              <InputErrorObserver />
              {children}
              <Box display="flex" justifyContent="space-between" className="mt-auto">
                <Button startIcon={<ChevronLeft />} disabled={isFirst} onClick={onBack}>
                  Previous Step
                </Button>
                <Button
                  disabled={disableNext || isLoading}
                  onClick={handleSubmit}
                  type="submit"
                  variant="contained"
                  endIcon={!isLast && <ChevronRight />}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <CircularProgress className="mr-2" size="1.5rem" />{' '}
                      {isDesktop ? 'Submitting please wait' : 'Submitting'}
                    </span>
                  ) : isLast ? (
                    'Submit Application'
                  ) : isDesktop ? (
                    'Continue To Next Step'
                  ) : (
                    'Next Step'
                  )}
                </Button>
              </Box>
            </div>
          </>
        );
      }}
    </Formik>
  );
}
